import type { ProfileResponse } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { SkyPage } from '../../components/SkyPage';
import { da } from '../../i18n/da';
import { useAuth } from '../auth/auth-context';
import { AvatarEditor } from './AvatarEditor';
import { DisplayNameForm } from './DisplayNameForm';
import { fetchProfile } from './profile-api';
import { useAvatarArt } from './useAvatarArt';
import styles from './ProfilePage.module.css';

/** The player's own page, opened from the start page: the name the others see and the figure. */
export function ProfilePage() {
  const navigate = useNavigate();
  const { signedIn } = useAuth();
  const art = useAvatarArt();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchProfile().then((result) => {
      if (cancelled) return;
      if (result.ok) setProfile(result.data);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SkyPage>
      <Card className={styles.card} aria-labelledby="profile-title">
        <div className={styles.header}>
          <h2 id="profile-title" className={styles.title}>
            {da.profile.title}
          </h2>
          <Button size="small" variant="quiet" onClick={() => void navigate('/')}>
            ◀ {da.profile.back}
          </Button>
        </div>

        {failed && <p className={styles.hint}>{da.profile.loadFailed}</p>}
        {!failed && (!profile || !art) && <p className={styles.hint}>{da.profile.loading}</p>}

        {profile && (
          <DisplayNameForm
            user={profile.user}
            onRenamed={(user) => {
              setProfile({ ...profile, user });
              signedIn(user);
            }}
          />
        )}
        {profile && art && (
          <AvatarEditor
            art={art}
            wardrobe={profile.wardrobe}
            balance={profile.balance}
            onSaved={(wardrobe, balance = profile.balance) =>
              setProfile((current) => current && { ...current, wardrobe, balance })
            }
          />
        )}
      </Card>
    </SkyPage>
  );
}
